// Shared editor for the per-user defaults (firm footer C26, default party labels C25, client-explainer link C30).
// Used by both the Start-page "Settings" tab and the Profile "Settings" tab, so the fields live in one place.
import { useState } from 'react';
import { useAuth } from '../auth/authStore';
import { LABEL_OPTIONS } from './model';

export function FirmDefaultsForm() {
  const firm = useAuth((s) => s.firm);
  const saveFirm = useAuth((s) => s.saveFirm);
  const [name, setName] = useState(firm.name ?? '');
  const [line1, setLine1] = useState(firm.line1 ?? '');
  const [line2, setLine2] = useState(firm.line2 ?? '');
  const [labelPref, setLabelPref] = useState(firm.labelPref ?? 'auto');
  const [clientLink, setClientLink] = useState(firm.clientLink ?? '');
  const [clientLinkLabel, setClientLinkLabel] = useState(firm.clientLinkLabel ?? '');
  const [state, setState] = useState<'idle' | 'saving' | 'saved'>('idle');
  const touch = () => setState('idle');
  const dirty = name !== (firm.name ?? '') || line1 !== (firm.line1 ?? '') || line2 !== (firm.line2 ?? '')
    || labelPref !== (firm.labelPref ?? 'auto') || clientLink !== (firm.clientLink ?? '') || clientLinkLabel !== (firm.clientLinkLabel ?? '');

  async function save() {
    setState('saving');
    try {
      await saveFirm({ ...firm, name: name.trim(), line1: line1.trim(), line2: line2.trim(), labelPref, clientLink: clientLink.trim(), clientLinkLabel: clientLinkLabel.trim() });
      setState('saved');
    } catch { setState('idle'); }
  }

  return (
    <div className="fds">
      <div className="fds-sec">
        <h4>Firm details on reports</h4>
        <p className="fds-hint">Shown in the footer of every report you print. Leave blank for no footer.</p>
        <label className="fds-field"><span>Firm name</span>
          <input value={name} onChange={(e) => { setName(e.target.value); touch(); }} placeholder="e.g. Rivera Family Law, APC" /></label>
        <label className="fds-field"><span>Address line</span>
          <input value={line1} onChange={(e) => { setLine1(e.target.value); touch(); }} placeholder="e.g. 100 Main St, Suite 200, San José CA 95113" /></label>
        <label className="fds-field"><span>Contact line</span>
          <input value={line2} onChange={(e) => { setLine2(e.target.value); touch(); }} placeholder="e.g. (408) 555-0100 · intake@riverafamilylaw.com · Bar #123456" /></label>
      </div>

      <div className="fds-sec">
        <h4>Default party labels</h4>
        <p className="fds-hint">How new cases label the two parties. “Automatic” uses the first names you enter, and otherwise falls back to Father/Mother (or Husband/Wife). You can still change labels per case.</p>
        <label className="fds-field"><span>New-case default</span>
          <select value={labelPref} onChange={(e) => { setLabelPref(e.target.value); touch(); }}>
            {LABEL_OPTIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select></label>
      </div>

      <div className="fds-sec">
        <h4>Client explainer link</h4>
        <p className="fds-hint">An optional link to a page that explains the numbers to your client. Shown at the bottom of client-facing reports (the client financial summary and income &amp; expense worksheet) only.</p>
        <label className="fds-field"><span>Link URL</span>
          <input value={clientLink} onChange={(e) => { setClientLink(e.target.value); touch(); }} placeholder="https://riverafamilylaw.com/understanding-support" /></label>
        <label className="fds-field"><span>Link text (optional)</span>
          <input value={clientLinkLabel} onChange={(e) => { setClientLinkLabel(e.target.value); touch(); }} placeholder="Understanding this report" /></label>
      </div>

      <div className="fds-actions">
        <button type="button" className="btn" onClick={save} disabled={state === 'saving' || !dirty}>
          {state === 'saving' ? 'Saving…' : state === 'saved' && !dirty ? 'Saved ✓' : 'Save settings'}
        </button>
      </div>
    </div>
  );
}
